import { useState, useRef, useCallback } from 'react'
import { confirmDialog } from 'primereact/confirmdialog'
import { useReassignment, type ReassignResult } from '../../hooks/useReassignment.js'
import { api, type ApiError } from '../../api/client.js'
import { useToast } from '../system/ToastProvider.js'
import type { BulkActionTarget, BulkLifecycleStatus, BulkResolutionType } from './ReassignDrawer.js'

const STATUS_ENDPOINT: Record<BulkLifecycleStatus, string> = {
  open: 'reopen',
  on_hold: 'on-hold',
  closed: 'close',
}

const STATUS_LABEL: Record<BulkLifecycleStatus, string> = {
  open: 'reopened',
  on_hold: 'put on hold',
  closed: 'closed',
}

/** Readable reason for a failed per-conversation status change. */
function statusFailureReason(err: unknown): string {
  const e = err as Partial<ApiError>
  const body = (typeof e.body === 'object' && e.body !== null ? e.body : {}) as { error?: string; message?: string }
  switch (body.error) {
    case 'OPEN_CHILD_TICKETS': return 'has open child tickets'
    case 'CONVERSATION_NOT_ASSIGNED_TO_YOU': return 'not assigned to you'
    case 'REQUIRED_FIELD_MISSING': return 'required close fields are missing'
    case 'NOT_FOUND':
    case 'CONVERSATION_NOT_FOUND': return 'not found'
    default: return body.message ?? body.error ?? (e.status === 403 ? 'permission denied' : e.message ?? 'failed')
  }
}

/**
 * Applies a lifecycle status to each conversation through the same endpoints the
 * conversation page uses (close / on-hold / reopen), so every per-ticket rule —
 * assignee guard, required close fields, open-child check — still holds.
 * A parent refuses to close while a child is open; when both are in the
 * selection the parent is retried after the children have closed.
 */
async function applyLifecycleStatus(
  ids: string[],
  status: BulkLifecycleStatus,
  resolutionType: BulkResolutionType,
): Promise<{ successCount: number; errorCount: number; reasons: string[] }> {
  const body = status === 'closed' ? { resolutionType } : {}
  const failures = new Map<string, string>()
  let successCount = 0
  let pending = ids
  while (pending.length > 0) {
    const retry: string[] = []
    let progressed = false
    for (let i = 0; i < pending.length; i += 10) {
      const chunk = pending.slice(i, i + 10)
      const outcomes = await Promise.allSettled(
        chunk.map((id) => api.post(`/conversations/${id}/${STATUS_ENDPOINT[status]}`, body)),
      )
      outcomes.forEach((outcome, index) => {
        const id = chunk[index] as string
        if (outcome.status === 'fulfilled') {
          successCount++
          progressed = true
          failures.delete(id)
          return
        }
        const code = ((outcome.reason as Partial<ApiError>).body as { error?: string } | undefined)?.error
        if (status === 'closed' && code === 'OPEN_CHILD_TICKETS') retry.push(id)
        failures.set(id, statusFailureReason(outcome.reason))
      })
    }
    // Only another pass if something closed this time — otherwise the
    // remaining parents' open children aren't in the selection.
    pending = progressed ? retry : []
  }
  const counts = new Map<string, number>()
  for (const reason of failures.values()) counts.set(reason, (counts.get(reason) ?? 0) + 1)
  return {
    successCount,
    errorCount: failures.size,
    reasons: [...counts].map(([reason, n]) => `${n} ${reason}`),
  }
}

export interface UseReassignDrawerControllerResult {
  drawerOpen: boolean
  openDrawer: (conversationIds: string[], currentAssigneeAgentIds?: string[]) => void
  closeDrawer: () => void
  selectedCount: number
  selectedConversationIds: string[]
  /** Agents already assigned to the selected conversations — disabled in the
   *  agent dropdown so a reassign can't be a no-op. */
  currentAssigneeAgentIds: string[]
  submitting: boolean
  handleSubmit: (target: BulkActionTarget) => Promise<void>
  handleTransfer: (target: { toTeamId: string; toAgentId?: string; description?: string }) => Promise<void>
  onSuccess: (callback: () => void) => void
}

/**
 * Shared drawer-open/submit/toast wiring for ReassignDrawer, used by both the
 * standalone Reassignment page and the Inbox's Reassign / Bulk Action
 * actions — the drawer's own field logic (team/agent/campaign cascades)
 * stays in ReassignDrawer itself and is untouched by this hook.
 */
export function useReassignDrawerController(): UseReassignDrawerControllerResult {
  const toast = useToast()
  const { reassigning, reassign, capacityBlock: _capacityBlock, clearCapacityBlock } = useReassignment()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [conversationIds, setConversationIds] = useState<string[]>([])
  const [currentAssigneeAgentIds, setCurrentAssigneeAgentIds] = useState<string[]>([])
  const [transferring, setTransferring] = useState(false)
  const [changingStatus, setChangingStatus] = useState(false)
  const onSuccessRef = useRef<(() => void) | null>(null)

  const openDrawer = useCallback((ids: string[], assigneeAgentIds: string[] = []) => {
    setConversationIds(ids)
    setCurrentAssigneeAgentIds(assigneeAgentIds)
    setDrawerOpen(true)
  }, [])

  const closeDrawer = useCallback(() => setDrawerOpen(false), [])

  const onSuccess = useCallback((callback: () => void) => {
    onSuccessRef.current = callback
  }, [])

  const finish = useCallback(async (
    result: ReassignResult | null,
    lifecycle: { status: BulkLifecycleStatus; resolutionType: BulkResolutionType } | null,
  ): Promise<void> => {
    if (result) {
      if (result.errorCount > 0) {
        toast.warning('Bulk action completed with errors', `${result.successCount} succeeded, ${result.errorCount} failed`)
      } else {
        toast.success('Bulk action applied', `${result.successCount} conversation(s) updated`)
      }
    }
    if (lifecycle) {
      setChangingStatus(true)
      try {
        const outcome = await applyLifecycleStatus(conversationIds, lifecycle.status, lifecycle.resolutionType)
        const verb = STATUS_LABEL[lifecycle.status]
        if (outcome.errorCount > 0) {
          toast.warning(
            'Status change completed with errors',
            `${outcome.successCount} ${verb}, ${outcome.errorCount} failed (${outcome.reasons.join(', ')})`,
          )
        } else {
          toast.success('Status updated', `${outcome.successCount} conversation(s) ${verb}`)
        }
      } finally {
        setChangingStatus(false)
      }
    }
    setDrawerOpen(false)
    onSuccessRef.current?.()
  }, [toast, conversationIds])

  const handleSubmit = useCallback(async (target: BulkActionTarget): Promise<void> => {
    // The lifecycle status isn't a reassign field (that endpoint's schema is
    // strict) — it's applied per conversation once the reassign has landed.
    const { lifecycleStatus, resolutionType, ...reassignTarget } = target
    const lifecycle = lifecycleStatus
      ? { status: lifecycleStatus, resolutionType: resolutionType ?? 'resolved' as const }
      : null
    if (Object.keys(reassignTarget).length === 0) {
      if (lifecycle) await finish(null, lifecycle)
      return
    }
    const outcome = await reassign(conversationIds, reassignTarget)
    if (outcome.kind === 'ok') {
      await finish(outcome.result, lifecycle)
      return
    }
    if (outcome.kind === 'error') return
    // The agent's conversation limit stopped this. It's a cap, not a prohibition —
    // show the numbers and let the supervisor decide, rather than silently
    // failing or silently overloading someone.
    clearCapacityBlock()
    confirmDialog({
      header: 'Agent is at their conversation limit',
      message: outcome.block.message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Assign anyway',
      rejectLabel: 'Cancel',
      accept: async () => {
        const forced = await reassign(conversationIds, reassignTarget, { force: true })
        if (forced.kind === 'ok') await finish(forced.result, lifecycle)
      },
    })
  }, [conversationIds, reassign, clearCapacityBlock, finish])

  const handleTransfer = useCallback(async (target: { toTeamId: string; toAgentId?: string; description?: string }): Promise<void> => {
    setTransferring(true)
    try {
      const outcomes = await Promise.allSettled(conversationIds.map((conversationId) =>
        api.post(`/conversations/${conversationId}/transfer`, {
          transferType: 'cold',
          toTeamId: target.toTeamId,
          ...(target.toAgentId ? { toAgentId: target.toAgentId } : {}),
          reason: 'Bulk transfer',
          ...(target.description?.trim() ? { description: target.description.trim() } : {}),
          isPrivate: true,
        }),
      ))
      const successCount = outcomes.filter((outcome) => outcome.status === 'fulfilled').length
      const errorCount = outcomes.length - successCount
      if (errorCount > 0) {
        toast.warning('Bulk transfer completed with errors', `${successCount} succeeded, ${errorCount} failed. Check that required conversation properties are complete and try the failed conversations again.`)
      } else {
        toast.success('Bulk transfer completed', `${successCount} conversation(s) transferred`)
      }
      setDrawerOpen(false)
      if (successCount > 0) onSuccessRef.current?.()
    } finally {
      setTransferring(false)
    }
  }, [conversationIds, toast])

  return {
    drawerOpen,
    openDrawer,
    closeDrawer,
    selectedCount: conversationIds.length,
    selectedConversationIds: conversationIds,
    currentAssigneeAgentIds,
    submitting: reassigning || transferring || changingStatus,
    handleSubmit,
    handleTransfer,
    onSuccess,
  }
}
